// Bas-U-pärmen: statusöversikt över utförandedokumenten med samlad export.
import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import DocumentHeader from "./DocumentHeader";
import { FolderOpen, FileDown, CheckCircle2, CircleDashed } from "lucide-react";
import {
  generateEquipmentInspectionsWordDocument,
  generateThirdPartyPlanWordDocument,
  generateTrafficPlanWordDocument,
  generateHazmatSurveyWordDocument,
  generateIsolationsWordDocument,
  generateOutagePlansWordDocument,
  generateSafetyRulesWordDocument,
  generateInductionsWordDocument,
  generateHotWorkPermitsWordDocument,
  generateExecutionFollowupWordDocument,
  generateSafetyDataSheetsWordDocument,
} from "@/lib/documentGenerator/basuExecutionGenerators";

interface Props {
  projectId: string;
}

interface Entry {
  docNumber: string;
  title: string;
  table: string;
  count: number;
  run: (projectId: string) => Promise<void>;
}

const DEFS: { docNumber: string; title: string; table: string; run: (projectId: string) => Promise<void> }[] = [
  {
    docNumber: "06b",
    title: "Besiktning och kontroll av utrustning",
    table: "equipment_inspections",
    run: async (projectId) => {
      const { data } = await supabase
        .from("equipment_inspections")
        .select("*")
        .eq("project_id", projectId);
      await generateEquipmentInspectionsWordDocument(projectId, (data ?? []) as never);
    },
  },
  {
    docNumber: "07",
    title: "Tredje man och pågående verksamhet",
    table: "third_party_plans",
    run: async (projectId) => {
      const { data } = await supabase
        .from("third_party_plans")
        .select("status, content_json, last_stage_review_at, last_stage_review_by_name")
        .eq("project_id", projectId)
        .maybeSingle();
      await generateThirdPartyPlanWordDocument(projectId, (data?.content_json ?? {}) as never, {
        status: data?.status,
        lastReviewAt: data?.last_stage_review_at ?? null,
        lastReviewBy: data?.last_stage_review_by_name ?? null,
      });
    },
  },
  {
    docNumber: "07b",
    title: "TA-plan – trafikanordning",
    table: "traffic_plans",
    run: async (projectId) => {
      const { data } = await supabase.from("traffic_plans").select("*").eq("project_id", projectId);
      await generateTrafficPlanWordDocument(projectId, (data ?? []) as never);
    },
  },
  {
    docNumber: "08",
    title: "Farligt material och sanering",
    table: "hazmat_surveys",
    run: async (projectId) => {
      const { data: survey } = await supabase
        .from("hazmat_surveys")
        .select("*")
        .eq("project_id", projectId)
        .maybeSingle();
      const { data: items } = await supabase
        .from("hazmat_items")
        .select("*")
        .eq("project_id", projectId);
      await generateHazmatSurveyWordDocument(projectId, (survey ?? {}) as never, (items ?? []) as never);
    },
  },
  {
    docNumber: "09",
    title: "Frånkoppling och återinkoppling",
    table: "electrical_isolations",
    run: async (projectId) => {
      const { data } = await supabase.from("electrical_isolations").select("*").eq("project_id", projectId);
      await generateIsolationsWordDocument(projectId, (data ?? []) as never);
    },
  },
  {
    docNumber: "09b",
    title: "Avbrottsplan – el, gas och media",
    table: "utility_outage_plans",
    run: async (projectId) => {
      const { data } = await supabase.from("utility_outage_plans").select("*").eq("project_id", projectId);
      await generateOutagePlansWordDocument(projectId, (data ?? []) as never);
    },
  },
  {
    docNumber: "15",
    title: "Skydds- och ordningsregler",
    table: "safety_rule_documents",
    run: async (projectId) => {
      const { data: doc } = await supabase
        .from("safety_rule_documents")
        .select("id, name, intro, status")
        .eq("project_id", projectId)
        .order("created_at")
        .limit(1)
        .maybeSingle();
      const { data: items } = doc
        ? await supabase
            .from("safety_rule_items")
            .select("category, text, legal_reference, included, sort_order")
            .eq("document_id", doc.id)
            .order("sort_order")
        : { data: [] };
      await generateSafetyRulesWordDocument(projectId, (doc ?? {}) as never, (items ?? []) as never);
    },
  },
  {
    docNumber: "15c",
    title: "Säkerhetsintroduktion per person",
    table: "safety_inductions",
    run: async (projectId) => {
      const { data } = await supabase.from("safety_inductions").select("*").eq("project_id", projectId);
      await generateInductionsWordDocument(projectId, (data ?? []) as never);
    },
  },
  {
    docNumber: "16b",
    title: "Heta arbeten – tillstånd",
    table: "hot_work_permits",
    run: async (projectId) => {
      const { data } = await supabase.from("hot_work_permits").select("*").eq("project_id", projectId);
      await generateHotWorkPermitsWordDocument(projectId, (data ?? []) as never);
    },
  },
  {
    docNumber: "21",
    title: "Uppföljning av utförandet",
    table: "execution_followup_items",
    run: async (projectId) => {
      const { data } = await supabase
        .from("execution_followup_items")
        .select("*")
        .eq("project_id", projectId)
        .order("sort_order");
      await generateExecutionFollowupWordDocument(
        projectId,
        ((data ?? []) as { status: string }[]).map((r) => ({ ...r, statusLabel: r.status })) as never
      );
    },
  },
  {
    docNumber: "26",
    title: "Kemikalieförteckning och säkerhetsdatablad",
    table: "safety_data_sheets",
    run: async (projectId) => {
      const { data } = await supabase.from("safety_data_sheets").select("*").eq("project_id", projectId);
      await generateSafetyDataSheetsWordDocument(projectId, (data ?? []) as never);
    },
  },
];

const BasuDocumentBinder: React.FC<Props> = ({ projectId }) => {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const counts = await Promise.all(
      DEFS.map(async (d) => {
        const { count } = await supabase
          .from(d.table as never)
          .select("id", { count: "exact", head: true })
          .eq("project_id", projectId);
        return { ...d, count: count ?? 0 };
      })
    );
    setEntries(counts);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const exportOne = async (entry: Entry) => {
    setBusy(entry.docNumber);
    try {
      await entry.run(projectId);
    } catch {
      toast.error(`Kunde inte skapa Dok ${entry.docNumber}`);
    } finally {
      setBusy(null);
    }
  };

  const exportAll = async () => {
    setBusy("alla");
    let failed = 0;
    for (const entry of entries.filter((e) => e.count > 0)) {
      try {
        await entry.run(projectId);
      } catch {
        failed += 1;
      }
    }
    setBusy(null);
    if (failed) toast.error(`${failed} dokument kunde inte skapas`);
    else toast.success("Bas-U-pärmen är nedladdad");
  };

  const ready = entries.filter((e) => e.count > 0).length;
  const percent = entries.length ? Math.round((ready / entries.length) * 100) : 0;

  return (
    <div className="space-y-4">
      <DocumentHeader
        title="Bas-U dokument"
        description="Alla utförandedokument samlade. Se vad som är ifyllt och ladda ner hela pärmen inför skyddsrond, inspektion eller överlämning."
        icon={FolderOpen}
        projectId={projectId}
        meta={
          <Badge variant={ready === entries.length ? "secondary" : "outline"}>
            {ready} av {entries.length} dokument har innehåll
          </Badge>
        }
        actions={
          <Button size="sm" onClick={exportAll} disabled={loading || busy !== null || !ready}>
            <FileDown className="mr-2 h-4 w-4" />
            {busy === "alla" ? "Skapar…" : "Exportera hela pärmen"}
          </Button>
        }
      />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Innehåll i pärmen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Progress value={percent} />
          {loading ? (
            <p className="text-sm text-muted-foreground">Laddar…</p>
          ) : (
            <ul className="divide-y">
              {entries.map((e) => (
                <li key={e.docNumber} className="flex flex-wrap items-center gap-3 py-3">
                  {e.count > 0 ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  ) : (
                    <CircleDashed className="h-4 w-4 text-muted-foreground" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      Dok {e.docNumber} · {e.title}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {e.count > 0 ? `${e.count} poster registrerade` : "Inget innehåll ännu"}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={busy !== null || e.count === 0}
                    onClick={() => exportOne(e)}
                  >
                    <FileDown className="mr-2 h-4 w-4" />
                    {busy === e.docNumber ? "Skapar…" : "Exportera"}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BasuDocumentBinder;
